// AnalyticsPage.tsx - Analytics dashboard with charts, KPIs, and filters
import { useState } from 'react';
import {
  Container,
  Title,
  Text,
  Stack,
  Group,
  Button,
  Paper,
  Select,
  Grid,
} from '@mantine/core';
import { DatePickerInput, DatesRangeValue } from '@mantine/dates';
import { IconDownload, IconRefresh, IconCalendar } from '@tabler/icons-react';
import { useDashboardAnalytics } from '@/hooks/useLeads';
import { DashboardStats } from '@/components/features/dashboard/DashboardStats/DashboardStats';
import { LeadsChart } from '@/components/features/dashboard/LeadsChart/LeadsChart';
import { SourcesChart } from '@/components/features/dashboard/SourcesChart/SourcesChart';

export function AnalyticsPage() {
  const [dateRange, setDateRange] = useState<DatesRangeValue>([null, null]);
  const [sourceFilter, setSourceFilter] = useState<string | null>(null);
  const { data: analytics, isLoading, refetch } = useDashboardAnalytics();

  const sourceOptions = [
    { value: 'all', label: 'All Sources' },
    { value: 'autoscout24', label: 'AutoScout24' },
    { value: 'mobile-de', label: 'Mobile.de' },
    { value: 'ss-com', label: 'SS.com' },
    { value: 'ebay-motors', label: 'eBay Motors' },
    { value: 'facebook', label: 'Facebook Marketplace' },
    { value: 'manual', label: 'Manual' },
  ];

  const handleExport = () => {
    // TODO: Implement export functionality
    console.log('Export analytics data');
  };

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <div>
            <Title order={1}>Analytics</Title>
            <Text c="dimmed" mt="xs">
              Insights and performance metrics
            </Text>
          </div>
          <Group>
            <Button
              variant="outline"
              leftSection={<IconRefresh size={16} />}
              onClick={() => refetch()}
            >
              Refresh
            </Button>
            <Button
              variant="outline"
              leftSection={<IconDownload size={16} />}
              onClick={handleExport}
            >
              Export
            </Button>
          </Group>
        </Group>

        {/* Filters */}
        <Paper p="md" radius="md" withBorder>
          <Group>
            <DatePickerInput
              type="range"
              label="Date Range"
              placeholder="Select date range"
              value={dateRange}
              onChange={setDateRange}
              leftSection={<IconCalendar size={16} />}
            />
            <Select
              label="Source"
              placeholder="Filter by source"
              data={sourceOptions}
              value={sourceFilter}
              onChange={setSourceFilter}
              clearable
            />
            {(dateRange[0] || dateRange[1] || sourceFilter) && (
              <Button
                variant="subtle"
                mt="auto"
                onClick={() => {
                  setDateRange([null, null]);
                  setSourceFilter(null);
                }}
              >
                Clear Filters
              </Button>
            )}
          </Group>
        </Paper>

        {/* KPI Stats */}
        {analytics?.data?.stats && (
          <DashboardStats stats={analytics.data.stats} isLoading={isLoading} />
        )}

        {/* Charts */}
        <Grid>
          <Grid.Col span={{ base: 12, md: 8 }}>
            {analytics?.data?.leadsOverTime && (
              <LeadsChart
                data={analytics.data.leadsOverTime}
                isLoading={isLoading}
              />
            )}
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 4 }}>
            {analytics?.data?.leadsBySource && (
              <SourcesChart
                data={analytics.data.leadsBySource}
                isLoading={isLoading}
              />
            )}
          </Grid.Col>
        </Grid>

        {/* Additional Analytics Sections */}
        <Grid>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Paper p="md" radius="md" withBorder>
              <Title order={3} mb="md">
                Conversion Funnel
              </Title>
              {analytics?.data?.stats ? (
                <Stack gap="sm">
                  <div>
                    <Group justify="space-between" mb="xs">
                      <Text size="sm">New Leads</Text>
                      <Text fw={600}>{analytics.data.stats.new_leads}</Text>
                    </Group>
                    <div style={{ height: 8, backgroundColor: '#e0e0e0', borderRadius: 4 }}>
                      <div
                        style={{
                          height: '100%',
                          width: '100%',
                          backgroundColor: '#4caf50',
                          borderRadius: 4,
                        }}
                      />
                    </div>
                  </div>
                  <div>
                    <Group justify="space-between" mb="xs">
                      <Text size="sm">Contacted</Text>
                      <Text fw={600}>{analytics.data.stats.contacted_leads}</Text>
                    </Group>
                    <div style={{ height: 8, backgroundColor: '#e0e0e0', borderRadius: 4 }}>
                      <div
                        style={{
                          height: '100%',
                          width: `${(analytics.data.stats.contacted_leads / analytics.data.stats.new_leads) * 100}%`,
                          backgroundColor: '#2196f3',
                          borderRadius: 4,
                        }}
                      />
                    </div>
                  </div>
                  <div>
                    <Group justify="space-between" mb="xs">
                      <Text size="sm">Qualified</Text>
                      <Text fw={600}>{analytics.data.stats.qualified_leads || 0}</Text>
                    </Group>
                    <div style={{ height: 8, backgroundColor: '#e0e0e0', borderRadius: 4 }}>
                      <div
                        style={{
                          height: '100%',
                          width: `${((analytics.data.stats.qualified_leads || 0) / analytics.data.stats.new_leads) * 100}%`,
                          backgroundColor: '#ff9800',
                          borderRadius: 4,
                        }}
                      />
                    </div>
                  </div>
                  <div>
                    <Group justify="space-between" mb="xs">
                      <Text size="sm">Converted</Text>
                      <Text fw={600}>{analytics.data.stats.converted_leads}</Text>
                    </Group>
                    <div style={{ height: 8, backgroundColor: '#e0e0e0', borderRadius: 4 }}>
                      <div
                        style={{
                          height: '100%',
                          width: `${(analytics.data.stats.converted_leads / analytics.data.stats.new_leads) * 100}%`,
                          backgroundColor: '#9c27b0',
                          borderRadius: 4,
                        }}
                      />
                    </div>
                  </div>
                </Stack>
              ) : (
                <Text c="dimmed">No data available</Text>
              )}
            </Paper>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 6 }}>
            <Paper p="md" radius="md" withBorder>
              <Title order={3} mb="md">
                Performance Metrics
              </Title>
              {analytics?.data?.stats ? (
                <Stack gap="md">
                  <div>
                    <Group justify="space-between">
                      <Text size="sm" c="dimmed">
                        Average Lead Score
                      </Text>
                      <Text fw={600}>{analytics.data.stats.average_score}</Text>
                    </Group>
                  </div>
                  <div>
                    <Group justify="space-between">
                      <Text size="sm" c="dimmed">
                        Conversion Rate
                      </Text>
                      <Text fw={600}>{analytics.data.stats.conversion_rate.toFixed(1)}%</Text>
                    </Group>
                  </div>
                  <div>
                    <Group justify="space-between">
                      <Text size="sm" c="dimmed">
                        Total Leads
                      </Text>
                      <Text fw={600}>{analytics.data.stats.total_leads}</Text>
                    </Group>
                  </div>
                </Stack>
              ) : (
                <Text c="dimmed">No data available</Text>
              )}
            </Paper>
          </Grid.Col>
        </Grid>
      </Stack>
    </Container>
  );
}
